import React from 'react';
import dollars from '../../dollar-format';
import {renderPrintFooter} from "../../utils";
import {executeQuery} from "../../requests/executeQuery";
import {STRIVE_INVENTORY_V2,STRIVE_INVENTORY_V3,STRIVE_INVENTORY_BREAKDOWN_V1,STRIVE_INVENTORY_NEGUC_V1} from '../../consts/queries';

// Breakdown rows shared by the In Negotiation and Under Contract tables
// (Claritask 545604 feedback). `type` narrows Retail into Single-/Multi-Tenant;
// Office and Industrial are category-only.
const NEGUC_SEGMENTS = [
    {label: 'Retail - MT', category: 'Retail', type: 'MT'},
    {label: 'Retail - STNL', category: 'Retail', type: 'STNL'},
    {label: 'Office', category: 'Office'},
    {label: 'Industrial', category: 'Industrial'},
];

export default class Inventory extends React.Component {
	constructor(props) {
	    super(props);
		this.state = {
            rows: {
                STNL: {},
                MT: {},
                Other: {},
                Total: {}            
            },
            breakdownRows: {
                Retail: {},
                Office: {},
                Industrial: {},
                Apartments: {},
                BreakdownTotal: {}
            },
            // In Negotiation / Under Contract breakdowns (Source Summit OR Buyer
            // Agent Firm Summit). Claritask 545604 — each table splits into the
            // same four Type segments plus a Total.
            negRows: {},
            ucRows: {},
            isPrint: false,
            printBackground: '',
            isLoading: true,
		};

        this.beforePrint = this.beforePrint.bind(this);
        this.afterPrint = this.afterPrint.bind(this);
	}

    static buildRowData(data, type) {
        const totalProperties = parseInt(data.TOTAL_PROPERTIES);
        const totalFee = parseFloat(data.TOTAL_FEE);
        const aveGrossFee = totalFee / totalProperties;
        return {
            type: type,
            properties: totalProperties,
            totalPrice: parseFloat(data.TOTAL_PRICE),
            totalFee: totalFee,
            aveGrossFee: aveGrossFee,
            aveNetFee: aveGrossFee / 2,
        };
    }

    static buildTotalData(rows) {
	    const dataToSum = {...rows};
        delete dataToSum.Total;

        Object.keys(dataToSum).forEach(key => {
            if (!dataToSum[key].properties) delete dataToSum[key];
        });

        const properties =  Object.keys(dataToSum).reduce((previous, key) => previous + dataToSum[key].properties, 0);
	    const totalPrice = Object.keys(dataToSum).reduce((previous, key) => previous + dataToSum[key].totalPrice, 0);
	    const totalFee = Object.keys(dataToSum).reduce((previous, key) => previous + dataToSum[key].totalFee, 0);
	    const aveGrossFee = totalFee / properties;

        return {
            type: 'Total',
            properties: properties,
            totalPrice: totalPrice,
            totalFee: totalFee,
            aveGrossFee: aveGrossFee,
            aveNetFee: aveGrossFee / 2,
        };
    }

    static buildBreakdownRowData(data, category) {
         const totalBreakdownProperties = parseInt(data.TOTAL_PROPERTIES);
        const totalBreakdownFee = parseFloat(data.TOTAL_FEE);
        const aveBreakdownGrossFee = totalBreakdownFee / totalBreakdownProperties;
        return {
            category: category,
            breakdownProperties: totalBreakdownProperties,
            totalBreakdownPrice: parseFloat(data.TOTAL_PRICE),
            totalBreakdownFee: totalBreakdownFee,
            aveBreakdownGrossFee: aveBreakdownGrossFee,
            aveBreakdownNetFee: aveBreakdownGrossFee / 2,
        };
    }
    static buildBreakdownTotalData(breakdownRows) {
        const dataToSum = {...breakdownRows};
        delete dataToSum.BreakdownTotal;

        Object.keys(dataToSum).forEach(key => {
            if (!dataToSum[key].breakdownProperties) delete dataToSum[key];
        });

        const breakdownProperties =  Object.keys(dataToSum).reduce((previous, key) => previous + dataToSum[key].breakdownProperties, 0);
	    const totalBreakdownPrice = Object.keys(dataToSum).reduce((previous, key) => previous + dataToSum[key].totalBreakdownPrice, 0);
	    const totalBreakdownFee = Object.keys(dataToSum).reduce((previous, key) => previous + dataToSum[key].totalBreakdownFee, 0);
	    const aveBreakdownGrossFee = totalBreakdownFee / breakdownProperties;

        return {
            category: 'Total',
            breakdownProperties: breakdownProperties,
            totalBreakdownPrice: totalBreakdownPrice,
            totalBreakdownFee: totalBreakdownFee,
            aveBreakdownGrossFee: aveBreakdownGrossFee,
            aveBreakdownNetFee: aveBreakdownGrossFee / 2,
        };
    }

    /**
     * Combined In Neg + Under Contract totals (Claritask 547889 #3): one row
     * that adds the two status tables together. Averages are recomputed from
     * the combined figures rather than averaged, so they stay meaningful.
     * Returns null when neither status has loaded yet.
     */
    static buildNegUcCombinedTotal(negRows, ucRows) {
        const parts = {};
        if (negRows && negRows.Total) parts['In Negotiation'] = negRows.Total;
        if (ucRows && ucRows.Total) parts['Under Contract'] = ucRows.Total;
        if (!Object.keys(parts).length) return null;

        return Inventory.buildTotalData(parts);
    }

    /**
     * Build a four-segment breakdown table (plus Total) for one NegUC status.
     * Each segment is its own aggregate query so the rows line up with the
     * Active tables. Segments that returned no row render as zeroes.
     */
    static buildNegUcRows(results, status) {
        const rows = {};
        NEGUC_SEGMENTS.forEach((seg, i) => {
            const res = results[i];
            rows[seg.label] = (res && res.success && Array.isArray(res.data) && res.data.length)
                ? Inventory.buildRowData(res.data[0], seg.label)
                : {type: seg.label, properties: 0, totalPrice: 0, totalFee: 0, aveGrossFee: 0, aveNetFee: 0};
        });
        rows.Total = Inventory.buildTotalData(rows);
        return rows;
    }

	fetch() {
        this.setState({isLoading: true});
        const negUcSegmentQueries = (status) => NEGUC_SEGMENTS.map(seg =>
            executeQuery({queryName: STRIVE_INVENTORY_NEGUC_V1, params: {STATUS: status, CATEGORY: seg.category, TYPE: seg.type || ''}}));
        Promise.all([
            executeQuery({queryName: STRIVE_INVENTORY_V2, params: {TYPE: 'STNL'}}),
            executeQuery({queryName: STRIVE_INVENTORY_V2, params: {TYPE: 'MT'}}),
            executeQuery({queryName: STRIVE_INVENTORY_V3, params: {TYPE: 'Other'}}),
            executeQuery({queryName: STRIVE_INVENTORY_BREAKDOWN_V1, params: {CATEGORY: 'Retail'}}),
            executeQuery({queryName: STRIVE_INVENTORY_BREAKDOWN_V1, params: {CATEGORY: 'Office'}}),
            executeQuery({queryName: STRIVE_INVENTORY_BREAKDOWN_V1, params: {CATEGORY: 'Industrial'}}),
            executeQuery({queryName: STRIVE_INVENTORY_BREAKDOWN_V1, params: {CATEGORY: 'Apartments'}}),
            Promise.all(negUcSegmentQueries('In Negotiation')),
            Promise.all(negUcSegmentQueries('Under Contract')),

        ]).then(res => {
            // In Neg / Under Contract breakdowns are independent of the Active
            // tables — build them whenever their queries returned, without
            // gating the whole page on them.
            const negUpdate = {};
            if (Array.isArray(res[7])) {
                negUpdate.negRows = Inventory.buildNegUcRows(res[7], 'In Negotiation');
            }
            if (Array.isArray(res[8])) {
                negUpdate.ucRows = Inventory.buildNegUcRows(res[8], 'Under Contract');
            }
            if (Object.keys(negUpdate).length) this.setState(negUpdate);
            if (res && res[0] && res[0].success && Array.isArray(res[0].data) && res[0].data.length &&
                res[1] && res[1].success && Array.isArray(res[1].data) && res[1].data.length &&
                res[2] && res[2].success && Array.isArray(res[2].data) && res[2].data.length &&
                res[3] && res[3].success && Array.isArray(res[3].data) && res[3].data.length &&
                res[4] && res[4].success && Array.isArray(res[4].data) && res[4].data.length &&
                res[5] && res[5].success && Array.isArray(res[5].data) && res[5].data.length &&
                res[6] && res[6].success && Array.isArray(res[6].data) && res[6].data.length ) {
                const rows = {...this.state.rows};
                const breakdownRows = {...this.state.breakdownRows};
                const stnlData = res[0].data[0];
                const mtData = res[1].data[0];
                const otherData = res[2].data[0];

                const retailData = res[3].data[0];
                const officeData = res[4].data[0];
                const industrialData = res[5].data[0];
                const apartmentsData = res[6].data[0];

                rows.STNL = Inventory.buildRowData(stnlData, 'STNL');
                rows.MT = Inventory.buildRowData(mtData, 'MT');
                rows.Other = Inventory.buildRowData(otherData, 'Other');
                rows.Total = Inventory.buildTotalData(rows);
                breakdownRows.Retail = Inventory.buildBreakdownRowData(retailData, 'Retail');
                breakdownRows.Office = Inventory.buildBreakdownRowData(officeData, 'Office');
                breakdownRows.Industrial = Inventory.buildBreakdownRowData(industrialData, 'Industrial');
                breakdownRows.Apartments = Inventory.buildBreakdownRowData(apartmentsData, 'Apartments');
                breakdownRows.BreakdownTotal = Inventory.buildBreakdownTotalData(breakdownRows);
                this.setState({rows, breakdownRows, isLoading: false});
            } else {
                this.setState({isLoading: false});
            }
        }).catch(() => {
            this.setState({isLoading: false});
        });
	}

	componentDidMount() {
        this.setState({printBackground: `url('${arc.path.media+'Transaction_Background.png'}')`}, this.fetch);
        window.onbeforeprint = this.beforePrint;
        window.onafterprint = this.afterPrint;
	}

    beforePrint() {
        this.setState({isPrint: true});
    }

    afterPrint() {
        this.setState({isPrint: false});
    }

    /**
     * Breakdown table for In Negotiation / Under Contract (Claritask 545604).
     * A title row names the status ("box header"), the column header carries
     * "Type", and the body lists the four segments plus a Total. Same 6 columns
     * and classes as the Active tables so all four tables line up. Total Fee here
     * comes from SUMMIT Fee - Auto (POTENTIAL_FEE), supplied by the backend query.
     */
    static renderNegUcTable(title, countLabel, rows, className = '') {
        const rowsObj = rows || {};
        const keys = Object.keys(rowsObj);

        // The dark rule sits ABOVE the Total row, the way the Active tables draw
        // it (Claritask 547889 feedback). With collapsed borders the edge is owned
        // by whatever sits directly above Total — the last segment row, or the
        // column header when Total is the only row (In Neg/Under Contract).
        const totalIndex = keys.indexOf('Total');
        const rowAboveTotal = totalIndex > 0 ? totalIndex - 1 : -1;
        const headerDrawsRule = totalIndex === 0;

        return (
            <table className={`table${className ? ` ${className}` : ''}`}>
                <thead>
                <tr>
                    <th className="Inventory_th Inventory_neguc_title" colSpan={6}>{title}</th>
                </tr>
                <tr className={headerDrawsRule ? 'Inventory_total' : ''}>
                    <th className="Inventory_th">Type</th>
                    <th className="Inventory_th">{countLabel}</th>
                    <th className="Inventory_th">Total Price</th>
                    <th className="Inventory_th">Total Fee</th>
                    <th className="Inventory_th">Ave Gross Fee</th>
                    <th className="Inventory_th">Ave Net Fee</th>
                </tr>
                </thead>
                <tbody>
                {keys.map((key, index) => {
                    const r = rowsObj[key];
                    return (
                        <tr key={key} className={index === rowAboveTotal ? 'Inventory_total' : ''}>
                            <td className="Inventory_td">{r.type}</td>
                            <td className="Inventory_column_numeric">{r.properties || 0}</td>
                            <td className="Inventory_column_numeric">{r.totalPrice ? dollars(r.totalPrice) : '$0'}</td>
                            <td className="Inventory_column_numeric">{r.totalFee ? dollars(r.totalFee, true) : '$0'}</td>
                            <td className="Inventory_column_numeric">{r.aveGrossFee ? dollars(r.aveGrossFee, true) : '$0'}</td>
                            <td className="Inventory_column_numeric">{r.aveNetFee ? dollars(r.aveNetFee, true) : '$0'}</td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
        );
    }

	render() {
        const {rows, breakdownRows, negRows, ucRows, isPrint, printBackground, isLoading} = this.state;
        const negUcTotal = Inventory.buildNegUcCombinedTotal(negRows, ucRows);
        const d = new Date();
		const date = d.getMonth()+1+'/'+d.getDate()+'/'+d.getFullYear();

        if (isLoading) {
            return (
                <div className='Inventory'>
                    <div className='print-container'>
                        <div className="Inventory_head_left">
                            <h3 className='Inventory_h3'>SUMMIT Inventory</h3>
                            <h4 className='Inventory_h4'>{date}</h4>
                        </div>
                        <div style={{textAlign: 'center', padding: '60px 20px', fontSize: '16px', color: '#666'}}>
                            <div style={{marginBottom: '15px'}}>Loading inventory data...</div>
                            <div className="spinner" style={{margin: '0 auto'}}></div>
                        </div>
                    </div>
                </div>
            );
        }

		return (
            <div className='Inventory'>
                <div className='print-container' style={{backgroundImage: isPrint && printBackground}}>
                    <div className="Inventory_head_left">
                        <h3 className='Inventory_h3'>SUMMIT Inventory</h3>
                        <h4 className='Inventory_h4'>{date}</h4>
                    </div>
                    <table className='table'>
                        <thead>
                        {/* Box header naming the two Active tables. Only the first
                            carries it — the second is the same Active set broken
                            down by type instead of category. (Claritask 547889 #1) */}
                        <tr>
                            <th className="Inventory_th Inventory_neguc_title" colSpan={6}>Active</th>
                        </tr>
                        <tr>
                            <th className="Inventory_th">Category</th>
                            <th className="Inventory_th"># of Active Properties</th>
                            <th className="Inventory_th">Total Price</th>
                            <th className="Inventory_th">Total Fee</th>
                            <th className="Inventory_th">Ave Gross Fee</th>
                            <th className="Inventory_th">Ave Net Fee</th>
                        </tr>
                        </thead>
                        <tbody>
                        {
                            Object.keys(breakdownRows).map((key, index) => {
                                return (
                                    <tr key={key} className={index === Object.keys(breakdownRows).length - 2 ? "Inventory_total" : ''}>
                                        <td className="Inventory_td">{breakdownRows[key].category}</td>
                                        <td className="Inventory_column_numeric">{breakdownRows[key].breakdownProperties}</td>
                                        <td className="Inventory_column_numeric">{breakdownRows[key].totalBreakdownPrice ? dollars(breakdownRows[key].totalBreakdownPrice) : '$0'}</td>
                                        <td className="Inventory_column_numeric">{breakdownRows[key].totalBreakdownFee ? dollars(breakdownRows[key].totalBreakdownFee, true) : '$0'}</td>
                                        <td className="Inventory_column_numeric">{breakdownRows[key].aveBreakdownGrossFee ? dollars(breakdownRows[key].aveBreakdownGrossFee, true) : '$0'}</td>
                                        <td className="Inventory_column_numeric">{breakdownRows[key].aveBreakdownNetFee ? dollars(breakdownRows[key].aveBreakdownNetFee, true) : '$0'}</td>
                                    </tr>
                                );
                            })
                        }
                        </tbody>
                    </table>




                    <table className='table'>
                        <thead>
                        <tr>
                            <th className="Inventory_th">Type</th>
                            <th className="Inventory_th"># of Active Properties</th>
                            <th className="Inventory_th">Total Price</th>
                            <th className="Inventory_th">Total Fee</th>
                            <th className="Inventory_th">Ave Gross Fee</th>
                            <th className="Inventory_th">Ave Net Fee</th>
                        </tr>
                        </thead>
                        <tbody>
                        {
                            Object.keys(rows).map((key, index) => {
                                return (
                                    <tr key={key} className={index === Object.keys(rows).length - 2 ? "Inventory_total" : ''}>
                                        <td className="Inventory_td">{rows[key].type}</td>
                                        <td className="Inventory_column_numeric">{rows[key].properties}</td>
                                        <td className="Inventory_column_numeric">{rows[key].totalPrice ? dollars(rows[key].totalPrice) : '$0'}</td>
                                        <td className="Inventory_column_numeric">{rows[key].totalFee ? dollars(rows[key].totalFee, true) : '$0'}</td>
                                        <td className="Inventory_column_numeric">{rows[key].aveGrossFee ? dollars(rows[key].aveGrossFee, true) : '$0'}</td>
                                        <td className="Inventory_column_numeric">{rows[key].aveNetFee ? dollars(rows[key].aveNetFee, true) : '$0'}</td>
                                    </tr>
                                );
                            })
                        }
                        </tbody>
                    </table>

                    {/* Each status table is spaced off the one above it: first from
                        the pair of Active tables, then from each other.
                        (547889 #2 and its follow-up feedback) */}
                    {Inventory.renderNegUcTable('In Negotiation', '# of Properties In Neg', negRows, 'Inventory_table_gap')}
                    {Inventory.renderNegUcTable('Under Contract', '# of Properties Under Contract', ucRows, 'Inventory_table_gap')}
                    {negUcTotal && Inventory.renderNegUcTable(
                        'In Neg/Under Contract',
                        '# of Properties In Neg/Under Contract',
                        {Total: negUcTotal},
                        'Inventory_table_gap',
                    )}
                    {renderPrintFooter(isPrint)}
                </div>
                <div className='preload-print-background' style={{backgroundImage: printBackground}}/>
            </div>
		);
	}
}
